import { GitBranch } from 'lucide-react';
import { useBriefPopoutBody } from '../../hooks/useBriefPopoutBody';
import { sectionLabel } from '../../lib/note-doc';
import type { BranchDivergence } from '../../hooks/useTeamTodaySurface';

/*
 * The brief popout's BODY (phase-B §B1 / walk D3): a per-object structured summary, composed for
 * this reader at popout size — never the note lifted verbatim.
 *
 * Structure is the note's own `note_doc` (overview + only the sections that note actually has), so
 * the headings here are the SAME vocabulary the note detail page uses (SECTION_LABELS). Nothing is
 * precomputed into the brief payload; the fetch happens on open.
 *
 * Degrading: while the body is in flight the dialog still shows the card's description and its item
 * rows, and a failed fetch renders NOTHING here rather than an error — a popout that cannot compose
 * a body is a smaller popout, never a dead one.
 *
 * Color: every line of substance below is `text-prose-foreground`, NOT `text-muted-foreground`
 * (bug-615). This body is the one rendering of the brief-composed prose anywhere in the product —
 * it exists nowhere else in the SPA and nowhere in the Slack DM — and the muted token measures
 * 3.45:1 on the popover in the cream default theme, under the 4.5:1 AA floor. Muted stays the label
 * register elsewhere; contrast table lives in redesign.css.
 */

export function BriefPopoutBody({
  conversationId,
  title,
  tag,
  why,
  citationCount,
}: {
  conversationId: string;
  title?: string | null;
  tag?: string | null;
  why?: string | null;
  citationCount?: number | null;
}) {
  const { data, isPending, isError } = useBriefPopoutBody({ conversationId, title, tag, why, citationCount });

  if (isPending) {
    return (
      <div className="space-y-2" data-testid="popout-body-loading" aria-hidden="true">
        <span className="block h-3 w-1/2 rounded bg-muted/60" />
        <span className="block h-3 w-full rounded bg-muted/40" />
        <span className="block h-3 w-4/5 rounded bg-muted/40" />
      </div>
    );
  }
  // `?.` all the way down: this is a backend response, and a route that answers with a shape the
  // SPA did not expect must degrade to "no body", never crash the open dialog.
  const sections = (data?.sections ?? []).filter(s => (s?.bullets?.length ?? 0) > 0);
  const standfirst = data?.standfirst ?? null;
  if (isError || sections.length === 0) return null;

  return (
    <div className="brief-popout-body" data-testid="popout-body">
      {standfirst && <p className="text-[15px] leading-6">{standfirst}</p>}
      <div className={standfirst ? 'mt-4 space-y-4' : 'space-y-4'}>
        {/* Keyed by POSITION, not by kind: a note can legitimately repeat a kind (a legacy note with
          * no stored note_doc is parsed from compacted_text, which emits one section per header and
          * funnels every unrecognized header into key_details), and duplicate React keys let a
          * section be dropped or duplicated on re-render. */}
        {sections.map((section, s) => (
          <section key={`${section.kind}-${s}`}>
            <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-prose-foreground">
              {sectionLabel(section.kind)}
            </p>
            <ul className="mt-2 space-y-1.5">
              {section.bullets.map((bullet, i) => (
                <li key={`${section.kind}-${s}-${i}`} className="flex gap-2 text-sm leading-5 text-prose-foreground">
                  <span aria-hidden="true" className="text-prose-foreground/60">
                    ·
                  </span>
                  <span className="min-w-0 flex-1">{bullet}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

/*
 * The overlap popout's extra detail (walk D15) — restore what is real, and only what is real.
 *
 * `sharedEntities` is the "why am I seeing this"; `conflictHunk.filePath` comes from the open PR's
 * changed files; `branchDivergence` renders only when GitHub actually populated it. The three
 * line-content fields the contract used to carry (lineNumber/yourLine/theirLine) were never
 * derivable from GitHub REST and have been deleted — there is nothing here to render them from.
 *
 * `secondaryConversationId` is the other side of the overlap: until now the brief could tell you two
 * people had collided and give you no way to open the other person's half.
 */
export function OverlapDetail({
  sharedEntities,
  branchDivergence,
  secondaryConversationId,
  onNavNote,
}: {
  sharedEntities?: string[] | null;
  branchDivergence?: BranchDivergence | null;
  secondaryConversationId?: string | null;
  onNavNote: (conversationId: string) => void;
}) {
  const entities = (sharedEntities ?? []).filter(Boolean);
  const filePath = branchDivergence?.conflictHunk?.filePath ?? null;
  const branches =
    branchDivergence?.branchYours && branchDivergence?.branchTheirs
      ? `${branchDivergence.branchYours} vs ${branchDivergence.branchTheirs}`
      : null;
  const counts =
    branchDivergence?.aheadBy != null && branchDivergence?.behindBy != null
      ? `${branchDivergence.aheadBy} ahead / ${branchDivergence.behindBy} behind`
      : null;
  const state = branchDivergence?.mergeableState ?? null;
  const hasBranchRow = !!(branches || counts || state || filePath);

  if (entities.length === 0 && !hasBranchRow && !secondaryConversationId) return null;

  return (
    <div className="brief-overlap-detail mt-5 space-y-3 first:mt-0" data-testid="overlap-detail">
      {entities.length > 0 && (
        <div>
          <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-prose-foreground">In common</p>
          <span className="mt-2 flex flex-wrap gap-1.5">
            {entities.map(e => (
              <span
                key={e}
                className="rounded-full bg-muted px-2 py-0.5 font-['DM_Mono'] text-[10px] text-muted-foreground"
              >
                {e}
              </span>
            ))}
          </span>
        </div>
      )}
      {hasBranchRow && (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-prose-foreground">
          <GitBranch size={14} className="text-prose-foreground" />
          {branches && <span className="font-['DM_Mono'] text-[11px]">{branches}</span>}
          {counts && <span>{counts}</span>}
          {state && <span>{state}</span>}
          {filePath && (
            <span>
              conflict in <span className="font-['DM_Mono'] text-[11px]">{filePath}</span>
            </span>
          )}
        </div>
      )}
      {secondaryConversationId && (
        <button
          type="button"
          onClick={() => onNavNote(secondaryConversationId)}
          className="text-sm font-medium text-acc underline-offset-2 transition hover:underline"
        >
          Open the other side →
        </button>
      )}
    </div>
  );
}
